import { useState, useEffect, useCallback } from 'react';

interface UseConsentOptions {
  required: boolean;
  version: string;
  storageKey: string;
  remember: boolean;
}

export function useConsent({ required, version, storageKey, remember }: UseConsentOptions) {
  const readStoredConsent = useCallback(() => {
    if (!required) return true;
    if (typeof window === 'undefined') return false;
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (!raw) return false;
      const parsed = JSON.parse(raw) as { accepted?: boolean; version?: string };
      if (!parsed?.accepted) return false;
      if (version && parsed.version && parsed.version !== version) return false;
      return true;
    } catch {
      return false;
    }
  }, [required, storageKey, version]);

  const [hasConsent, setHasConsent] = useState<boolean>(() => readStoredConsent());

  // Re-validate stored consent whenever config changes
  useEffect(() => {
    setHasConsent(readStoredConsent());
  }, [required, storageKey, version, readStoredConsent]);

  // Persist consent decisions
  useEffect(() => {
    if (!required || !remember) return;
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({ accepted: hasConsent, version, timestamp: new Date().toISOString() })
      );
    } catch {
      // ignore storage errors
    }
  }, [required, remember, storageKey, version, hasConsent]);

  return { hasConsent, setHasConsent };
}
